import React, { useEffect, useState, useRef } from "react";
import RaagDetails from "./components/RaagDetails";
import FilterPanel from "./components/FilterPanel";
import SearchBar from "./components/SearchBar";
import Fuse from "fuse.js";
import RaagSunburst from "./components/RaagSunburst";
import SwaraPad from "./components/SwaraPad";

function App() {
  const [raagData, setRaagData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ Time: "", Season: "", Mood: "" });
  const [query, setQuery] = useState("");
  const [sunburstFilteredRaags, setSunburstFilteredRaags] = useState(null);
  const [showAllRaags, setShowAllRaags] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showSwaraPad, setShowSwaraPad] = useState(false);
  const [swaraVolume, setSwaraVolume] = useState(0.1);
  const [tablaVolume, setTablaVolume] = useState(0.2);

  const detailRef = useRef(null);

  const scrollToDetail = () => {
    if (detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleChartFilter = (path) => {
    const [Season, Time, Mood, raag] = path;
    const result = raagData.filter(r =>
      (!Season || r.Season === Season) &&
      (!Time || r.Time === Time) &&
      (!Mood || r.Mood.includes(Mood)) &&
      (!raag || r.Name === raag)
    );
    setFiltered(result);
    setSunburstFilteredRaags(null);
    setSelected(null);
  };

  const handleRaagClick = (raagName) => {
    const raag = raagData.find(r => r.Name === raagName);
    if (raag) {
      setSelected(raag);
      setTimeout(scrollToDetail, 100);
    }
  };

  useEffect(() => {
    fetch("/raag.json")
      .then((res) => res.json())
      .then((data) => {
        setRaagData(data);
        setFiltered(data);
      });
  }, []);

  useEffect(() => {
    let res = raagData;

    if (filters.Time) res = res.filter((r) => r.Time === filters.Time);
    if (filters.Season) res = res.filter((r) => r.Season === filters.Season);
    if (filters.Mood) res = res.filter((r) => r.Mood.includes(filters.Mood));

    if (query.trim() !== "") {
      const fuse = new Fuse(res, {
        keys: ["Name", "Thaat", "Arohana", "Avrohana", "Mood"],
                threshold: 0.3,
      });
      res = fuse.search(query).map((result) => result.item);
    }

    setFiltered(res);
    setSelected(null);
  }, [filters, query, raagData]);

  return (
    <div className="w-full px-4 max-w-screen-xl mx-auto text-center relative flex flex-col min-h-screen">
      
      {/* 🎵 Header */}
      <header className="py-8 mb-4 border-b border-gray-200">
      <h1 className="text-5xl font-serif text-black-1000 drop-shadow-sm border-b-2 border-black-500 inline-block pb-2">राग</h1>
        <p className="text-sm text-gray-500 mt-1"></p> {/* desc */}
      </header>

      <main className="flex-grow">
        {/* 🌞 Sunburst */}
        <div className="w-full px-4 md:px-12 lg:px-20 xl:px-32 2xl:px-40 mt-4">
          <div className="w-full max-w-screen-2xl mx-auto">
            <RaagSunburst
              raags={filtered}
              highlightedRaag={selected?.Name}
              onFilterSelect={handleChartFilter}
              onRaagSelect={handleRaagClick}
              muted={muted}
            />
          </div>
        </div>

        {/* 🔍 Search */}
        <div className="my-6 max-w-xs mx-auto">
          <SearchBar
            onSearch={setQuery}
            value={query}
            placeholderTyping={["Yaman", "Bhairav", "Desh", "Kafi"]}
            className="w-full max-w-sm text-sm py-1 px-3 border border-gray-300 rounded"
          />
        </div>

        <button
          onClick={() => {
            setQuery("");
            setFilters({ Time: "", Season: "", Mood: "" });
            setSelected(null);
            setSunburstFilteredRaags(null);
            setFiltered(raagData);
          }}
          className="bg-gray-200 hover:bg-gray-300 text-sm px-4 py-2 rounded mb-4"
        >
          Reset
        </button>

        <div className="flex justify-center">
          <div className="text-center max-w-2xl w-full flex flex-col items-center">
            <FilterPanel filters={filters} setFilters={setFilters} raagData={raagData} />
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-2">Raags</h2>
          <ul className="space-y-2 max-w-md mx-auto">
          {Array.from(
              new Map(
                (sunburstFilteredRaags || filtered).map((r) => [r.Name, r])
              ).values()
            )
              .slice(0, showAllRaags ? 10 : 5)
              .map((r) => (

                <li
                  key={r.Name}
                  className="p-2 border rounded cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setSelected(r);
                    setTimeout(scrollToDetail, 100);
                  }}
                >
                  {r.Name}
                </li>
              ))}
          </ul>
          {(sunburstFilteredRaags || filtered).length > 5 && (
            <button
              className="text-sm text-blue-600 mt-2 hover:underline"
              onClick={() => setShowAllRaags(!showAllRaags)}
            >
              {showAllRaags ? "Show Less" : "Show More"}
            </button>
          )}
        </div>

        {selected && (
          <div ref={detailRef} className="mt-8 max-w-2xl mx-auto">
            <RaagDetails raag={selected} />
          </div>
        )}
      </main>

      {/* 🔇 Mute Button */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setMuted(!muted)}
          className="bg-white rounded-full shadow-lg p-2 border border-gray-300 hover:bg-gray-100"
          title={muted ? "Unmute" : "Mute"}
        >
          {muted ? "🔇" : "🔊"}
        </button>
      </div>

      {/* 🎼 Music Button */}
      <div className="fixed bottom-4 right-16 z-50">
        <button
          onClick={() => setShowSwaraPad(!showSwaraPad)}
          className="bg-white rounded-full shadow-lg p-2 border border-gray-300 hover:bg-gray-100"
          title="Toggle SwaraPad"
        >
          🎼
        </button>
      </div>

      {/* 🎵 Swara Pad Panel */}
      {showSwaraPad && (
  <div className="fixed bottom-20 right-4 z-40 bg-white p-4 rounded shadow-lg border w-64">
    <SwaraPad tampuraVolume={swaraVolume} tablaVolume={tablaVolume} muted={muted} />

<div className="mt-3">
  <label className="text-sm font-medium mr-2">Tampura Volume:</label>
  <input
    type="range"
    min="0.01"
    max="0.5"
    step="0.01"
    value={swaraVolume}
    onChange={(e) => setSwaraVolume(parseFloat(e.target.value))}
    className="w-full accent-green-500"
    list="tampura-volume-ticks"
  />
  <datalist id="tampura-volume-ticks">
    {[...Array(10)].map((_, i) => (
      <option key={i} value={(i * 0.05 + 0.01).toFixed(2)} />
    ))}
  </datalist>
</div>

<div className="mt-3">
  <label className="text-sm font-medium mr-2">Tabla Volume:</label>
  <input
    type="range"
    min="0.01"
    max="0.5"
    step="0.01"
    value={tablaVolume}
    onChange={(e) => setTablaVolume(parseFloat(e.target.value))}
    className="w-full accent-orange-500"
    list="tabla-volume-ticks"
  />
  <datalist id="tabla-volume-ticks">
    {[...Array(10)].map((_, i) => (
      <option key={i} value={(i * 0.05 + 0.01).toFixed(2)} />
    ))}
  </datalist>
</div>

  </div>
)}


      {/* 📦 Footer */}
      <footer className="mt-16 py-8 text-center border-t text-gray-400 text-sm">
      ॐ {new Date().getFullYear()} Raag.Info — Let Your Soul Choose the Melody.
      </footer>
    </div>
  );
}

export default App;
